import { NotificationType } from '@app/components/common/BaseNotification/BaseNotification';
import { Priority } from '@app//constants/enums/priorities';
import visa from '@app/assets/images/card-issuers/visa.png';
import mastercard from '@app/assets/images/card-issuers/mastercard.png';
import maestro from '@app/assets/images/card-issuers/maestro.png';
import { CurrencyTypeEnum, Severity } from '@app/interfaces/interfaces';
import { BaseBadgeProps } from '@app/components/common/BaseBadge/BaseBadge';
import { currencies } from '@app/constants/config/currencies';
import moment from 'moment';

export const camelize = (string: string): string => {
  return string
    .split(' ')
    .map((word, index) => (index === 0 ? word.toLowerCase() : word[0].toUpperCase() + word.slice(1)))
    .join('');
};

export const getCurrencyPrice = (price: number | string, currency: CurrencyTypeEnum, isIcon = true): string => {
  const currencySymbol = currencies[currency][isIcon ? 'icon' : 'text'];

  return isIcon ? `${currencySymbol}${price}` : `${price} ${currencySymbol}`;
};

type MarkArea = {
  xAxis: number;
};

export const getMarkAreaData = (data: string[] | number[]): MarkArea[][] =>
  data.map((el, index) => [
    {
      xAxis: 2 * index,
    },
    {
      xAxis: 2 * index + 1,
    },
  ]);

export const capitalize = (word: string): string => `${word[0].toUpperCase()}${word.slice(1)}`;

export const hexToRGB = (hex: string): string => {
  const r = parseInt(hex.slice(1, 3), 16),
    g = parseInt(hex.slice(3, 5), 16),
    b = parseInt(hex.slice(5, 7), 16);

  return `${r}, ${g}, ${b}`;
};

export const getDifference = (value: number, prevValue: number): string | null =>
  prevValue !== 0 ? `${((Math.abs(value - prevValue) / prevValue) * 100).toFixed(0)}%` : '100%';

export const normalizeProp = (prop: string | number | [number, number]): string =>
  typeof prop === 'number' ? `${prop}px` : (Array.isArray(prop) && `${prop[0]}px ${prop[1]}px`) || prop.toString();

export const defineColorByPriority = (priority: Priority): string => {
  switch (priority) {
    case Priority.INFO:
      return 'var(--primary-color)';
    case Priority.LOW:
      return 'var(--success-color)';
    case Priority.MEDIUM:
      return 'var(--warning-color)';
    case Priority.HIGH:
      return 'var(--error-color)';
    default:
      return 'var(--success-color)';
  }
};

export const defineColorBySeverity = (severity: NotificationType | undefined, rgb = false): string => {
  const postfix = rgb ? 'rgb-color' : 'color';
  switch (severity) {
    case 'error':
    case 'warning':
    case 'success':
      return `var(--${severity}-${postfix})`;
    case 'info':
    default:
      return `var(--primary-${postfix})`;
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const mergeBy = (a: any[], b: any[], key: string): any[] =>
  a.filter((elem) => !b.find((subElem) => subElem[key] === elem[key])).concat(b);

export const getSmoothRandom = (factor: number, start: number): number => {
  const halfEnvelope = 1 / factor / 2;
  const max = Math.min(1, start + halfEnvelope);
  const min = Math.max(0, start - halfEnvelope);

  return Math.random() * (max - min) + min;
};

export const shadeColor = (color: string, percent: number): string => {
  let R = parseInt(color.substring(1, 3), 16);
  let G = parseInt(color.substring(3, 5), 16);
  let B = parseInt(color.substring(5, 7), 16);

  R = parseInt(((R * (100 + percent)) / 100).toString());
  G = parseInt(((G * (100 + percent)) / 100).toString());
  B = parseInt(((B * (100 + percent)) / 100).toString());

  R = R < 255 ? R : 255;
  G = G < 255 ? G : 255;
  B = B < 255 ? B : 255;

  const RR = R.toString(16).length == 1 ? '0' + R.toString(16) : R.toString(16);
  const GG = G.toString(16).length == 1 ? '0' + G.toString(16) : G.toString(16);
  const BB = B.toString(16).length == 1 ? '0' + B.toString(16) : B.toString(16);

  return '#' + RR + GG + BB;
};

export const hexToHSL = (hex: string): { h: number; s: number; l: number } => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

  if (result) {
    let r = parseInt(result[1], 16);
    let g = parseInt(result[2], 16);
    let b = parseInt(result[3], 16);
    (r /= 255), (g /= 255), (b /= 255);
    const max = Math.max(r, g, b),
      min = Math.min(r, g, b);
    let h, s;
    const l = (max + min) / 2;
    if (max == min) {
      h = s = 0; // achromatic
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
        default:
          h = 0;
      }
      h /= 6;
    }
    return {
      h,
      s,
      l,
    };
  } else {
    throw new Error('Non valid HEX color');
  }
};

export const formatNumberWithCommas = (value: number | undefined | null): string => {
  if (value == null) {
    // Handles both undefined and null
    return ''; // Or return a default value like '0'
  }

  if (isNaN(value)) return '0';

  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export type AppCurrency = 'VND' | 'USD';

// Đơn vị tiền toàn app — AppSettingProvider nạp từ setting `currency` (tab Cửa
// hàng) khi khởi động/refresh. Module-level để các hàm format thuần dùng được
// ngoài React (cột bảng, chart config...).
let appCurrency: AppCurrency = 'VND';

export const setAppCurrency = (currency: string): void => {
  appCurrency = currency === 'USD' ? 'USD' : 'VND';
};

export const getAppCurrency = (): AppCurrency => appCurrency;

/**
 * Format tiền tệ theo setting `currency` của cửa hàng:
 * - VND (mặc định): "1,500,000 đ" (làm tròn đơn vị đồng)
 * - USD: "$1,500,000.50" (tối đa 2 số lẻ)
 * Dùng cho MỌI giá trị tiền trong bảng/thẻ/tổng kết — formatNumberWithCommas
 * chỉ dành cho số thường (số lượng, đếm) không mang đơn vị tiền.
 */
export const formatMoney = (value: number | undefined | null): string => {
  if (value == null) return '';
  const amount = Number(value);
  if (isNaN(amount)) return appCurrency === 'USD' ? '$0' : '0 đ';

  if (appCurrency === 'USD') {
    const sign = amount < 0 ? '-' : '';
    return `${sign}$${Math.abs(amount).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
  }
  return `${formatNumberWithCommas(Math.round(amount))} đ`;
};

export const msToH = (ms: number): number => Math.floor(ms / 3600000);

export const hToMS = (h: number): number => h * 3600000;

export const getPaymentCardTypeIcon = (type: string): string | null => {
  switch (type) {
    case 'visa':
      return visa;
    case 'mastercard':
      return mastercard;
    case 'maestro':
      return maestro;
    case 'amex':
      return 'amex';
    case 'discover':
      return 'discover';
    case 'diners':
      return 'diners';
    case 'jcb':
      return 'jcb';
    case 'unionpay':
      return 'unionpay';
    default:
      return null;
  }
};

export const mapBadgeStatus = (status: BaseBadgeProps['status']): Severity => {
  if (!status || status === 'default' || status === 'processing') {
    return 'info';
  }

  return status;
};

export const formatNumberInInput = (value: number | undefined): string => {
  if (value === undefined) {
    return '';
  }
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

//"01-12-2024 04:02 AM"
export const formatDateTime = (dateString: string, type="time") => {
  if (dateString === null) return;
  
  const date = new Date(dateString);

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
  const year = date.getFullYear();

  const hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');

  switch(type) {
    case "date":
      return `${day}-${month}-${year}`;

    case "time":
      const isAM = hours < 12;
      const formattedHours = String(hours % 12 || 12).padStart(2, '0');
      const meridian = isAM ? 'AM' : 'PM';

      return `${day}-${month}-${year} ${formattedHours}:${minutes} ${meridian}`;
    default:
      throw new Error(`Unsupported type: ${type}`);
  }
};

export const formatFileSize = (sizeInBytes: number): string => {
  const sizeInKB = sizeInBytes / 1024;
  return `${sizeInKB.toFixed(2)} KB`; // Round to 2 decimal places
};

const DIGITS: Record<number, string> = {
  0: 'không',
  1: 'một',
  2: 'hai',
  3: 'ba',
  4: 'bốn',
  5: 'năm',
  6: 'sáu',
  7: 'bảy',
  8: 'tám',
  9: 'chín',
};

const BLOCKS = ['', 'nghìn', 'triệu', 'tỷ', 'nghìn tỷ', 'triệu tỷ'];

function readBlock(block: string): string {
  if (block === '000') return '';
  
  let hundreds = parseInt(block[0]);
  let tens = parseInt(block[1]);
  let ones = parseInt(block[2]);
  
  let words = '';
  
  // Process hundreds
  if (hundreds > 0) {
    words += DIGITS[hundreds] + ' trăm ';
  }
  
  // Process tens
  if (tens > 0) {
    if (tens === 1) {
      words += 'mười ';
    } else {
      words += DIGITS[tens] + ' mươi ';
    }
  }
  
  // Process ones
  if (ones > 0) {
    if (tens === 0 && hundreds !== 0) {
      words += 'lẻ ';
    }
    
    if (ones === 1 && tens > 1) {
      words += 'mốt ';
    } else if (ones === 5 && tens > 0) {
      words += 'lăm ';
    } else {
      words += DIGITS[ones] + ' ';
    }
  }
  
  return words;
}

export function numberToWords(num: number): string {
  if (num === 0) return 'không';
  if (num < 0) return 'âm ' + numberToWords(Math.abs(num));
  
  // Convert number to string and pad with zeros to make length divisible by 3
  let numStr = Math.floor(num).toString();
  while (numStr.length % 3 !== 0) {
    numStr = '0' + numStr;
  }
  
  // Split into blocks of 3
  let blocks = numStr.match(/.{3}/g) || [];
  
  // Process each block
  let result = '';
  for (let i = 0; i < blocks.length; i++) {
    let blockWords = readBlock(blocks[i]);
    if (blockWords) {
      result += blockWords + BLOCKS[blocks.length - 1 - i] + ' ';
    }
  }
  
  // Clean up the result
  result = result
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
  
  // Capitalize first letter
  return result.charAt(0).toUpperCase() + result.slice(1);
}

export const joinWithArray = (obj: string | string[]) => {
  if (Array.isArray(obj)) {
    return obj.join(", ")
  }
  return obj
}

export const formatBookingDate = (date: Date) => {
  if (!date) return
  return moment(date, 'dd-MMM-yyyy h:mm aa');
}

export const formatDateToWord = (dateInput: string | number | Date) => {
  const now = new Date();
  const date = new Date(dateInput);

  // Check if the date is valid
  if (isNaN(date.getTime())) {
    console.error('Invalid date:', dateInput);
    return 'Invalid date';
  }

  const diff = now.getTime() - date.getTime();

  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 60) {
    return 'Vừa xong';
  } else if (minutes < 60) {
    return `${minutes} phút trước`;
  } else if (hours < 24) {
    return `${hours} giờ trước`;
  } else if (days === 1) {
    return 'Hôm qua';
  } else {
    return `${days} ngày trước`;
  }
};

// console.log(formatPhoneNumber('0977073155'));  // '84977073155'
// console.log(formatPhoneNumber('84977073155')); // '84977073155'
// console.log(formatPhoneNumber('+84977073155')); // '84977073155'

export const formatPhoneNumber = (phone: string): string => {
  // First, validate the input phone number
  if (!phone.match(/^(0|84|\+84)\d{9}$/)) {
    throw new Error('Invalid phone number format');
  }

  // If phone starts with 0, replace it with 84
  if (phone.startsWith('0')) {
    return `84${phone.slice(1)}`;
  }

  // If phone starts with +84, remove the +
  if (phone.startsWith('+84')) {
    return phone.slice(1);
  }

  // If phone already starts with 84, return as is
  return phone;
};

export const formatCurrency = (amount: number) => {
  // Theo setting `currency` toàn app (xem setAppCurrency phía trên).
  const isUsd = getAppCurrency() === 'USD';
  const formatter = new Intl.NumberFormat(isUsd ? 'en-US' : 'vi-VN', {
    style: 'currency',
    currency: isUsd ? 'USD' : 'VND',
    useGrouping: true,
    maximumFractionDigits: isUsd ? 2 : 0,
  });

  // Format and explicitly replace dots with commas if needed
  return formatter.format(amount).replace(/\./g, ',');
};

export const getNumberInString = (value: string) => {
  return parseInt(value?.match(/\d+/)?.[0] ?? '0');
};

export const formatBirthday = (value: any) => {
  let format = null;

  if (value === null) return null;

  if (typeof value === 'string') {
    const [day, month, year] = value.split('-');
    format = `${day}-${month}`;
  }

  if (value instanceof Date) {
    const birthday = new Date(value);
    const day = String(birthday.getDate()).padStart(2, '0');
    const month = String(birthday.getMonth()).padStart(2, '0');

    format = `${day}-${month}`;
  }

  return format;
}